import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  ShoppingCart, 
  Headphones, 
  Star,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useBooks } from '../hooks/useContent';

export default function Books() {
  // Fetch books from Contentful (with static fallback)
  const { books: booksData, isLoading, error } = useBooks();

  const featuredBook = booksData.find(book => book.featured);
  const otherBooks = booksData.filter(book => !book.featured);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-secondary" />
        <span className="ml-2 text-slate-600">Loading books...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">Failed to load books: {error}</p>
          <Button variant="outline" onClick={() => window.location.reload()} className="rounded-full">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="books-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-brand-accent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-brand-secondary font-semibold tracking-widest uppercase text-sm mb-4">
              My Books
            </p>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-brand-primary mb-6">
              Read. Learn. Transform.
            </h1>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Explore my collection of books designed to help you become a more effective leader
              and achieve breakthrough results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Book */}
      {featuredBook && (
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-8"
            >
              <span className="text-brand-secondary font-semibold tracking-widest uppercase text-sm">
                Featured Book
              </span>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
              {/* Book Cover */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex justify-center"
              >
                <Link to={`/books/${featuredBook.slug}`} className="relative group">
                  {/* 3D effect */}
                  <div className="absolute -right-4 top-4 bottom-4 w-4 bg-gradient-to-r from-slate-200 to-slate-100 rounded-r-lg" />
                  <div className="absolute -bottom-4 left-4 right-0 h-4 bg-gradient-to-b from-slate-200 to-slate-100 rounded-b-lg" />
                  <img
                    src={featuredBook.coverImage}
                    alt={featuredBook.title}
                    className="relative w-80 md:w-96 rounded-lg shadow-2xl group-hover:scale-105 transition-transform duration-300"
                  />
                </Link>
              </motion.div>

              {/* Book Details */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <Link to={`/books/${featuredBook.slug}`} className="group">
                  <h2 className="font-heading text-3xl md:text-5xl font-bold text-brand-primary mb-2 group-hover:text-brand-secondary transition-colors">
                    {featuredBook.title}
                  </h2>
                </Link>
                <p className="text-brand-secondary text-xl font-medium mb-6">
                  {featuredBook.subtitle}
                </p>
                <p className="text-slate-600 text-lg leading-relaxed mb-8">
                  {featuredBook.description}
                </p>

                {/* Book Meta */}
                <div className="flex flex-wrap gap-6 mb-8 text-sm text-slate-600">
                  <span className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-brand-secondary" />
                    {featuredBook.pages} pages
                  </span>
                  <span>Published: {featuredBook.publishDate}</span>
                  <span>{featuredBook.publisher}</span>
                </div>

                {/* Buy Buttons */}
                <div className="flex flex-wrap gap-4 mb-6">
                  {featuredBook.amazonUrl && (
                    <Button
                      asChild
                      size="lg"
                      className="rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90 font-semibold shadow-lg shadow-orange-500/20"
                    >
                      <a href={featuredBook.amazonUrl} target="_blank" rel="noopener noreferrer">
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        Buy on Amazon
                      </a>
                    </Button>
                  )}
                  {featuredBook.audibleUrl && (
                    <Button
                      asChild
                      size="lg"
                      variant="outline"
                      className="rounded-full border-2 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white font-semibold"
                    >
                      <a href={featuredBook.audibleUrl} target="_blank" rel="noopener noreferrer">
                        <Headphones className="w-5 h-5 mr-2" />
                        Audiobook
                      </a>
                    </Button>
                  )}
                </div>

                {/* Learn more link */}
                <Link
                  to={`/books/${featuredBook.slug}`}
                  className="inline-flex items-center text-brand-secondary font-medium hover:gap-2 transition-all"
                >
                  Learn more about this book <ArrowRight className="ml-1 w-4 h-4" />
                </Link>
              </motion.div>
            </div>

            {/* Testimonials Preview */}
            {featuredBook.testimonials && featuredBook.testimonials.length > 0 && (
              <div className="mt-20">
                <h3 className="font-heading text-2xl font-bold text-brand-primary mb-8 text-center">
                  What Readers Say
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {featuredBook.testimonials.slice(0, 3).map((testimonial, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-brand-accent rounded-2xl p-6 md:p-8"
                    >
                      <div className="flex gap-1 mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 fill-brand-secondary text-brand-secondary" />
                        ))}
                      </div>
                      <blockquote className="text-brand-primary font-medium text-lg mb-4">
                        "{testimonial.quote}"
                      </blockquote>
                      <cite className="not-italic">
                        <p className="font-semibold text-brand-primary">{testimonial.author}</p>
                        <p className="text-sm text-slate-600">{testimonial.title}</p>
                      </cite>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Other Books */}
      {otherBooks.length > 0 && (
        <section className="py-20 md:py-32 bg-brand-accent">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              className="mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-primary">
                More Books
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {otherBooks.map((book, index) => (
                <motion.div
                  key={book.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/books/${book.slug}`}
                    className="bg-white rounded-2xl p-6 md:p-8 flex flex-col md:flex-row gap-6 hover:shadow-xl transition-all duration-300 group block"
                  >
                    {/* Book Cover */}
                    <div className="flex-shrink-0">
                      <img
                        src={book.coverImage}
                        alt={book.title}
                        className="w-32 md:w-40 rounded-lg shadow-lg mx-auto md:mx-0 group-hover:scale-105 transition-transform"
                      />
                    </div>

                    {/* Book Info */}
                    <div className="flex-grow">
                      <h3 className="font-heading text-xl md:text-2xl font-bold text-brand-primary mb-1 group-hover:text-brand-secondary transition-colors">
                        {book.title}
                      </h3>
                      <p className="text-brand-secondary font-medium text-sm mb-3">
                        {book.subtitle}
                      </p>
                      <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                        {book.description}
                      </p>
                      <span className="inline-flex items-center text-brand-secondary font-medium text-sm group-hover:gap-2 transition-all">
                        Learn more <ArrowRight className="ml-1 w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
